import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, TextField, IconButton, Tooltip, Typography, InputAdornment, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { Add as AddIcon, Send as SendIcon } from '@mui/icons-material';
import { WorkflowBoxProps } from '../WorkflowBox/WorkflowBox';
import { textFieldSX } from '../../constants/textFieldCSS';
import ToolSelectionPopper from './ToolSelectionPopper';
import ToolConfigurationPopper from './ToolConfigurationPopper';
import { useWorkflowId } from '../../utils/misc';
import { useGetActionsForService } from '../../api/services';
import { 
    useGetAvailableActivities, 
    ActivityInfo, 
    useGetToolsForActivityNode,
    useSaveToolForActivityNode,
    useDeleteToolFromActivityNode,
    AgentTool
} from '../../api/activityNodeApi';
import { RunTestIcon } from '../../svgUrls';
import { NodeRunInstance, NodeTestRunInstance, RunInstance, WorkflowOutput, NodeConnection, Node, NodeExecutionCondition, WorkflowNode } from '../../types/workflow';
import AgentNodeTestSection from './AgentNodeTestSection';
import { useSaveNodeDataMappings, useGetConfigsForNode } from "../../api/nodeApi";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { InternalNodeType, NodeState } from '../../constants/workflow';
import TriggerSetup from '../WorkflowBox/TriggerSetup';
import { IdValueOption, ResponseConfigOption } from '../../types/common';
import { NodeConfig } from '../../types/nodes';
import { ServiceActions } from '../../types/service';

interface ChatMessage {
    role: 'user' | 'agent';
    content: string;
    timestamp: Date;
}

// Define the props for the AgentNode component
type AgentNodeProps = Omit<WorkflowBoxProps, 'updateNodeConfigsFromRunInstances'> & {
    testRunInputs?: {run_instance: RunInstance, node_run_instances: NodeRunInstance[]}[];
    selectedRunInstanceId?: string;
    setSelectedRunInstanceId?: (value: string) => void;
    refetchTestRunInputs?: (params: { nodeId: string, reset: boolean }, options?: { onSettled: () => void }) => void;
    updateNodeConfigsFromRunInstances?: (nodeId: string, response_config: string) => void;
    testRunInstance?: NodeTestRunInstance;
    testRunInstances?: Record<string, NodeTestRunInstance>;
    nodeResponseConfigs?: Record<string, Record<string, string>>;
    setNodeResponseConfigs?: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
    nodeIdsAbove?: {id: string, service: string, name: string}[];
    workflowNodes?: WorkflowNode[];
    saveConfigField?: (nodeId: string, key: string, refreshSavedRunConfig: (nodeId: string) => void, setIsFetchingSavedNodeConfig: (value: boolean) => void) => void;
};

const AgentNode: React.FC<AgentNodeProps> = ({
    workflowNode,
    nodeConfigs,
    setNodeConfigsForNodeInRef,
    getNodeConfigsForNodeFromRef,
    onNodeSave,
    workflowType,
    savedAction,
    testRunInputs = [],
    selectedRunInstanceId = '',
    setSelectedRunInstanceId,
    refetchTestRunInputs,
    updateNodeConfigsFromRunInstances,
    testRunInstance,
    testRunInstances,
    nodeResponseConfigs,
    setNodeResponseConfigs,
    nodeIdsAbove = [],
    workflowNodes = [],
    saveConfigField,
}) => {
    const [currentMessage, setCurrentMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [isToolSelectionOpen, setIsToolSelectionOpen] = useState(false);
    const [isToolConfigOpen, setIsToolConfigOpen] = useState(false);
    const [selectedTool, setSelectedTool] = useState<AgentTool | null>(null);
    const [toolSelectionAnchorEl, setToolSelectionAnchorEl] = useState<HTMLElement | null>(null);
    const [toolConfigAnchorEl, setToolConfigAnchorEl] = useState<HTMLElement | null>(null);
    const [configFields, setConfigFields] = useState<Record<string, string>>({});
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const workflowId = useWorkflowId();
    const { mutate: getActionsForServiceMutate } = useGetActionsForService();
    const { data: availableActivities, isLoading: isLoadingActivities } = useGetAvailableActivities();
    const saveNodeDataMappingsMutation = useSaveNodeDataMappings();
    const { mutate: fetchSavedNodeConfig } = useGetConfigsForNode();
    
    // API hooks for tool management
    const nodeId = workflowNode?.node?.id;
    const { data: tools = [], isLoading: isLoadingTools } = useGetToolsForActivityNode(nodeId);
    const saveToolMutation = useSaveToolForActivityNode();
    const deleteToolMutation = useDeleteToolFromActivityNode();

    // Test section state
    const [isTestSectionExpanded, setIsTestSectionExpanded] = useState(false);
    
    // Additional state for TriggerSetup
    const [isFetchingSavedNodeConfig, setIsFetchingSavedNodeConfig] = useState(false);
    const [openConditionsDetails, setConditionsOpenDetails] = useState(false);
    const [localConditions, setLocalConditions] = useState<NodeExecutionCondition[]>([]);
    const [responseConfig, setResponseConfig] = useState<IdValueOption[]>([]);
    const [isSomeTriggerAdded, setIsSomeTriggerAdded] = useState(false);
    const [nodeRunConfigHasKeys, setNodeRunConfigHasKeys] = useState(false);
    const [oneShotPopperAnchorEl, setOneShotPopperAnchorEl] = useState<HTMLElement | null>(null);
    const [actionsForService, setActionsForService] = useState<ServiceActions>({});
    const [countToNameMap, setCountToNameMap] = useState<Record<string, string>>({});
    const [filterText, setFilterText] = useState('');

    // Use refs to break dependency cycles
    const nodeConfigRef = useRef<Record<string, any>>({});
    const processingRef = useRef(false);
    const initialLoadRef = useRef(false);

    // Create a map of workflow nodes for easier reference
    const workflowNodesMap = React.useMemo(() => {
        if (!workflowNodes || workflowNodes.length === 0) {
            return {};
        }
        
        return workflowNodes.reduce((acc, node) => {
            const nodeId = node?.node?.id as string;
            if (nodeId) {
                acc[nodeId] = node;
            }
            return acc;
        }, {} as Record<string, any>);
    }, [workflowNodes]);

    // Load actions for service - only run once per workflowType
    useEffect(() => {
        if (workflowType) {
            getActionsForServiceMutate({ service: workflowType }, {
                onSuccess: (services) => setActionsForService(services)
            });
        }
    }, [workflowType, getActionsForServiceMutate]);

    // Simplified response config setup that doesn't depend on workflowNodesMap
    useEffect(() => {
        // Guard against processing while already in progress
        if (processingRef.current) return;
        
        const nodeIdsAboveLength = nodeIdsAbove?.length;
        if (!nodeIdsAboveLength || !nodeConfigs || !nodeResponseConfigs) return;
        
        try {
            processingRef.current = true;
            
            const responseConfigArray: ResponseConfigOption[] = [];
            const newCountToNameMap: Record<string, string> = {};
            
            // Nodes are already sorted from furthest to nearest
            nodeIdsAbove.forEach((presentNode, index) => {
                if (!presentNode || !presentNode.id) return;
                
                const _responseConfig = nodeResponseConfigs[presentNode.id];
                if (_responseConfig && Object.keys(_responseConfig)?.length) {
                    Object.entries(_responseConfig).forEach(([configKey, configValue]) => {
                        if (!configKey) return;
                        
                        responseConfigArray.push({
                            id: configKey,
                            value: configValue || "",
                            valueDetail: presentNode.service || "",
                            count: index + 1,
                            refNodeId: presentNode.id,
                            nodeName: presentNode?.name || "",
                        });
                    });
                } else {
                    // Only add placeholder for non-filter/delay services
                    if (presentNode.service && ["filter", "delay"].indexOf(presentNode.service) === -1) {
                        responseConfigArray.push({
                            id: "",
                            value: "",
                            valueDetail: presentNode.service || "",
                            count: index + 1,
                            refNodeId: presentNode.id,
                            nodeName: presentNode?.name || "",
                        });
                    }
                }
                
                // Get node name from map
                const nodeName = workflowNodesMap[presentNode.id]?.node?.name || '';
                newCountToNameMap[index + 1] = nodeName;
            });
            
            setCountToNameMap(newCountToNameMap);
            setResponseConfig(responseConfigArray);
        } finally {
            processingRef.current = false;
        }
    }, [nodeResponseConfigs, nodeIdsAbove, nodeConfigs]);

    // Replace refreshNodeConfig with refreshSavedRunConfig
    const refreshSavedRunConfig = (nodeId: string) => {
        if (!nodeId) return;
        
        setIsFetchingSavedNodeConfig(true);
        
        fetchSavedNodeConfig({ nodeId }, {
            onSuccess: (res) => {
                // Update node configs if available
                if (nodeConfigs) {
                    setNodeConfigsForNodeInRef(nodeId, res);
                }
                
                // Update node configs in ref
                setNodeConfigsForNodeInRef(nodeId, res);
                
                // Update local config fields
                setConfigFields(res.run_config || {});
                
                // Check if any trigger is added
                const nodeRunConfig = getNodeConfigsForNodeFromRef(nodeId)?.run_config;
                setIsSomeTriggerAdded(
                    nodeRunConfig && !!Object.keys(nodeRunConfig)?.length &&
                        Object.values(nodeRunConfig).some(value => value && String(value)?.trim())
                );
                setNodeRunConfigHasKeys(nodeRunConfig && !!Object.keys(nodeRunConfig)?.length);
                
                // Update response configs if available
                if (setNodeResponseConfigs && res.response_config) {
                    setNodeResponseConfigs(
                        (prev: Record<string, Record<string, string>>) => ({
                            ...prev,
                            [nodeId]: res.response_config
                        })
                    );
                }
            },
            onSettled: () => {
                setIsFetchingSavedNodeConfig(false);
            }
        });
    };

    // Update the useEffect that loads initial config to use refreshSavedRunConfig
    useEffect(() => {
        if (!nodeId || initialLoadRef.current) return;
        
        initialLoadRef.current = true;
        refreshSavedRunConfig(nodeId);
        
        return () => {
            initialLoadRef.current = false;
        };
    }, [nodeId]);

    // Auto-scroll to bottom of chat
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages]);

    // Cleanup effect to ensure all poppers are closed when the component unmounts
    useEffect(() => {
        return () => {
            setIsToolSelectionOpen(false);
            setToolSelectionAnchorEl(null);
            setIsToolConfigOpen(false);
            setToolConfigAnchorEl(null);
            setSelectedTool(null);
        };
    }, []);

    // Handle configuration field change
    const handleConfigFieldChange = (key: string, value: string) => {
        setConfigFields(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Set node run config and ref node id (similar to WorkflowBox)
    const setNodeRunConfigAndRefNodeId = (key: string, value: string, refNodeId?: string) => {
        if (nodeId) {
            const nodeConfigsForNode = getNodeConfigsForNodeFromRef(nodeId);
            setNodeConfigsForNodeInRef(nodeId, {
                ...nodeConfigsForNode,
                run_config: {
                    ...nodeConfigsForNode.run_config,
                    [key]: value
                },
                run_config_metadata: {
                    ...nodeConfigsForNode.run_config_metadata,
                    [key]: {
                        ...nodeConfigsForNode.run_config_metadata?.[key],
                        ref_node_id: refNodeId
                    }
                }
            });
            
            // Update local state
            setConfigFields(prev => ({
                ...prev,
                [key]: value
            }));
            
            // Check if any trigger is added
            const nodeRunConfig = getNodeConfigsForNodeFromRef(nodeId)?.run_config;
            setIsSomeTriggerAdded(
                nodeRunConfig && 
                !!Object.keys(nodeRunConfig)?.length &&
                Object.values(nodeRunConfig).some(value => value && String(value)?.trim())
            );
            setNodeRunConfigHasKeys(!!Object.keys(nodeRunConfig)?.length);
        }
    };

    // Save configuration on blur
    const handleConfigFieldBlur = (key: string) => {
        if (nodeId && saveConfigField) {
            // Use the saveConfigField method from props to save the configuration
            saveConfigField(nodeId, key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig);
        }
    };

    // Handler for adding a new configuration field
    const handleAddConfigField = () => {
        const newFieldKey = `config_${Object.keys(configFields).length + 1}`;
        setConfigFields(prev => ({
            ...prev,
            [newFieldKey]: ''
        }));
    };

    // Handler for removing a configuration field
    const handleRemoveConfigField = (key: string) => {
        const newConfigFields = { ...configFields };
        delete newConfigFields[key];
        
        if (nodeId) {
            const nodeConfig = getNodeConfigsForNodeFromRef(nodeId);
            const newRunConfig = { ...nodeConfig.run_config };
            delete newRunConfig[key];
            
            setNodeConfigsForNodeInRef(nodeId, {
                ...nodeConfig,
                run_config: newRunConfig
            });
            
            setConfigFields(newConfigFields);
            
            // Save to backend
            onNodeSave(false, nodeId, savedAction || 'AgentAction', workflowType, refreshSavedRunConfig, () => {
                console.log('Agent configuration saved after field removal');
            });
        }
    };

    // Handle sending a new message
    const handleSendMessage = () => {
        if (currentMessage.trim()) {
            const newMessage: ChatMessage = {
                role: 'user',
                content: currentMessage.trim(),
                timestamp: new Date()
            };
            
            setChatMessages(prev => [...prev, newMessage]);
            setCurrentMessage('');
            
            // Simulate agent response (in a real app, this would call an API)
            setTimeout(() => {
                const agentResponse: ChatMessage = {
                    role: 'agent',
                    content: 'I received your message. I can help with that using the tools available to me.',
                    timestamp: new Date()
                };
                setChatMessages(prev => [...prev, agentResponse]);
                saveToNodeConfig();
            }, 1000);
        }
    };

    // Handle opening tool selection
    const handleOpenToolSelection = (event: React.MouseEvent<HTMLElement>) => {
        setToolSelectionAnchorEl(event.currentTarget);
        setIsToolSelectionOpen(true);
    };

    // Handle closing tool selection
    const handleCloseToolSelection = () => {
        setToolSelectionAnchorEl(null);
        setIsToolSelectionOpen(false);
    };

    // Handle tool selection
    const handleToolSelect = (service: string, action: string) => {
        // Find the activity metadata
        const activity = availableActivities?.find(a => a.name === action);
        const serviceName = activity?.metadata?.service || service;
        
        const newTool: Partial<AgentTool> = {
            name: action,
            service: serviceName,
            action: action,
            config: {},
        };
        
        // Save the tool to the backend
        if (nodeId) {
            saveToolMutation.mutate({ 
                nodeId, 
                tool: newTool 
            }, {
                onSuccess: (data: AgentTool) => {
                    // Set the selected tool for configuration
                    setSelectedTool(data);
                    
                    // Close the tool selection popper
                    handleCloseToolSelection();
                    
                    // Open tool configuration
                    const panel = document.querySelector('.floating-workflow-panel') as HTMLElement;
                    if (panel) {
                        setToolConfigAnchorEl(panel);
                        setIsToolConfigOpen(true);
                    }
                }
            });
        }
    };

    // Handle tool configuration save
    const handleToolConfigSave = (toolId: string, config: Record<string, any>) => {
        if (nodeId) {
            // Find the tool to update
            const toolToUpdate = tools.find(t => t.id === toolId);
            
            if (toolToUpdate) {
                const updatedTool = {
                    ...toolToUpdate,
                    config
                };
                
                // Save the updated tool to the backend
                saveToolMutation.mutate({ 
                    nodeId, 
                    tool: updatedTool 
                });
            } else if (selectedTool) {
                // It's a new tool
                const newTool = {
                    ...selectedTool,
                    config
                };
                
                // Save the new tool to the backend
                saveToolMutation.mutate({ 
                    nodeId, 
                    tool: newTool 
                });
            }
        }
        
        // Clean up state
        setIsToolConfigOpen(false);
        setToolConfigAnchorEl(null);
        setSelectedTool(null);
    };

    // Handle editing an existing tool
    const handleEditTool = (tool: AgentTool) => {
        // First close any open poppers
        setIsToolSelectionOpen(false);
        setToolSelectionAnchorEl(null);
        
        // Then set up the tool configuration popper
        setSelectedTool(tool);
        const panel = document.querySelector('.floating-workflow-panel') as HTMLElement;
        if (panel) {
            setToolConfigAnchorEl(panel);
            setIsToolConfigOpen(true);
        }
    };

    // Handle removing a tool
    const handleRemoveTool = (toolId: string) => {
        if (nodeId) {
            deleteToolMutation.mutate({ 
                nodeId, 
                toolId 
            });
        }
    };

    // Save chat messages to node config
    const saveToNodeConfig = () => {
        if (workflowNode?.node?.id) {
            const nodeConfig = getNodeConfigsForNodeFromRef(workflowNode.node.id);
            setNodeConfigsForNodeInRef(workflowNode.node.id, {
                ...nodeConfig,
                run_config: configFields
            });

            // Save to backend
            onNodeSave(false, workflowNode.node.id, savedAction || 'AgentAction', workflowType, refreshSavedRunConfig, () => {
                console.log('Agent configuration saved');
            });
        }
    };

    // Update handleOnSave to use refreshSavedRunConfig
    const handleOnSave = (action?: string, cb?: () => void, isIntermittentSave?: boolean) => {
        if (workflowNode?.node?.id) {
            onNodeSave(
                isIntermittentSave || false, 
                workflowNode.node.id, 
                action || savedAction || 'AgentAction', 
                workflowType, 
                refreshSavedRunConfig, 
                cb
            );
        }
    };

    // Determine if any step is incomplete
    const isAnyStepIncomplete = Boolean(
        !savedAction ||
        (nodeRunConfigHasKeys && !isSomeTriggerAdded)
    );

    return (
        <div className="bg-white rounded-lg shadow-lg p-4 flex flex-col h-full">
            {/* Agent Header */}
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
                <h2 className="text-xl font-semibold">
                    {workflowNode?.node?.name || 'Agent'}
                </h2>
                <div className="flex gap-2">
                    {/* Removed the Add Tool button */}
                </div>
            </div>

            {/* Replace custom configuration section with TriggerSetup */}
            <div className="agent-config-section">
                <Typography variant="subtitle1" gutterBottom>
                    Configuration Fields
                </Typography>
                
                <TriggerSetup
                    nodeRunConfig={configFields}
                    setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                    nodeConfig={nodeId ? getNodeConfigsForNodeFromRef(nodeId) || {
                        run_config: {},
                        response_config: {},
                        type: InternalNodeType.InternalNodeTypeAction,
                        run_interval: 0,
                        run_config_render_data: {}
                    } : {
                        run_config: {},
                        response_config: {},
                        type: InternalNodeType.InternalNodeTypeAction,
                        run_interval: 0,
                        run_config_render_data: {}
                    }}
                    responseConfig={responseConfig}
                    chosenAction={savedAction || 'AgentAction'}
                    saveConfigField={handleConfigFieldBlur}
                    openConditionsDetails={openConditionsDetails}
                    localConditions={localConditions}
                    removeCondition={(index: number) => {
                        const newConditions = [...localConditions];
                        newConditions.splice(index, 1);
                        setLocalConditions(newConditions);
                    }}
                    setLocalConditions={setLocalConditions}
                    isAnyStepIncomplete={isAnyStepIncomplete}
                    setConditionsOpenDetails={setConditionsOpenDetails}
                    addNewCondition={handleAddConfigField}
                    onSave={handleOnSave}
                    workflowNode={workflowNode}
                    filterText={filterText}
                    countToNameMap={countToNameMap}
                    isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                    setOneShotPopperAnchorEl={(e) => setOneShotPopperAnchorEl(e.currentTarget)}
                    action={savedAction || 'AgentAction'}
                    service={workflowNode?.node?.service || 'agent'}
                />
            </div>

            {/* Tool management section */}
            <div className="agent-tools-section">
                <Typography variant="subtitle1" gutterBottom>
                    Available Tools
                    <Tooltip title="Add Tool">
                        <IconButton 
                            onClick={handleOpenToolSelection}
                            color="primary"
                            size="small"
                            sx={{ ml: 1 }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Typography>
                
                {isLoadingTools ? (
                    <CircularProgress size={24} />
                ) : tools.length > 0 ? (
                    <List dense>
                        {tools.map((tool) => (
                            <ListItem 
                                key={tool.id}
                                secondaryAction={
                                    <>
                                        <IconButton edge="end" onClick={() => handleEditTool(tool)} title="Edit Tool">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => handleRemoveTool(tool.id)} title="Remove Tool">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                }
                            >
                                <ListItemText 
                                    primary={tool.name} 
                                    secondary={`${tool.service} / ${tool.action}`} 
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        No tools added yet. Click the plus icon to add tools.
                    </Typography>
                )}
            </div>

            {/* Test Section */}
            <AgentNodeTestSection 
                nodeId={nodeId}
                workflowNode={workflowNode}
                testRunInputs={testRunInputs}
                selectedRunInstanceId={selectedRunInstanceId}
                setSelectedRunInstanceId={setSelectedRunInstanceId}
                refetchTestRunInputs={refetchTestRunInputs}
                updateNodeConfigsFromRunInstances={updateNodeConfigsFromRunInstances}
                isExpanded={isTestSectionExpanded}
                onClose={() => setIsTestSectionExpanded(!isTestSectionExpanded)}
            />

            {/* Tool Selection Popper */}
            <ToolSelectionPopper
                open={Boolean(toolSelectionAnchorEl)}
                anchorEl={toolSelectionAnchorEl}
                onClose={handleCloseToolSelection}
                onToolSelect={handleToolSelect}
                availableActivities={availableActivities}
                isLoadingActivities={isLoadingActivities}
            />

            {/* Tool Configuration Popper */}
            {selectedTool && (
                <ToolConfigurationPopper
                    open={isToolConfigOpen}
                    anchorEl={toolConfigAnchorEl}
                    onClose={() => {
                        setIsToolConfigOpen(false);
                        setToolConfigAnchorEl(null);
                        setSelectedTool(null);
                    }}
                    tool={selectedTool}
                    onSave={handleToolConfigSave}
                />
            )}
        </div>
    );
};

export default AgentNode;
